// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { observable, action, makeObservable, computed, runInAction } from 'mobx';
import { injectable } from 'inversify';

import container from '@core/di';
import CustomersService from '@shared/services/customers';
import { Customer, CustomerUser, CustomerDTO, CustomerUserDTO } from '@shared/models/customers';
import BaseStore from '@core/stores/base';
import { ListRequestParams, PageInfo } from '@shared/types/services';
import { getQueries } from '@shared/utils/common';

@injectable()
export default class CustomersStore extends BaseStore<CustomerDTO, Customer, CustomersService> {
  static diToken = Symbol('customers-store');
  @observable customerUsersList: Array<CustomerUser> = [];
  @observable _pageInfoUsers: PageInfo;

  constructor() {
    super({ service: container.get<CustomersService>(CustomersService.diToken) });
    makeObservable(this);
  }

  @computed get pageInfoUsers() {
    return this._pageInfoUsers;
  }

  @action getUsersList = async (customerId: CustomerDTO['id']) => {
    this.loading.list = true;

    const queries: ListRequestParams = getQueries() as unknown as ListRequestParams;

    try {
      const { items, pageInfo } = await this.service.getUsersList(customerId, queries);

      runInAction(() => {
        this.customerUsersList = items;
        this.loading.list = false;
      });
      
      this._pageInfoUsers = pageInfo;
    } catch (err) {
      runInAction(() => {
        this.loading.list = false;
      });
    }
  }

  @action getUser = async (customerId: CustomerDTO['id'], userId: CustomerUserDTO['id']) => {
    this.loading.item = true;

    try {
      const customer = await this.service.getUser(customerId, userId);
      this.loading.item = false;

      return customer;
    } catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  createUser = async (customerId: CustomerDTO['id'], data: CustomerUserDTO) => {
    try {
      await this.service.createUser(customerId, data);
      this.getUsersList(customerId);
    } catch (err) {
      throw err;
    }
  }

  editUser = async (customerId: CustomerDTO['id'], userId: CustomerUserDTO['id'], data: CustomerUserDTO) => {
    try {
      await this.service.editUser(customerId, userId, data);
      this.getUsersList(customerId);
    } catch (err) {
      throw err;
    }
  }

  deleteUser = async (customerId: CustomerDTO['id'], userId: CustomerUserDTO['id']) => {
    try {
      await this.service.deleteUser(customerId, userId);
      this.getUsersList(customerId);
    } catch (err) {
      throw err;
    }
  }

  resendInvitation(userId: CustomerUserDTO['id']) {
    return this.service.resendInvitation(userId);
  }
}